// Reinforce screen (P31, G-70): `/g/:galaxyId/fleet/reinforce?target=&mission=&arriveAt=`. The new
// fleet is sent as a timed order with the attack's arrival, so it lands in the same second.
import type { ShipType } from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router';
import { endpoints } from '../api/endpoints';
import { useReinforceOptions } from '../api/queries';
import { ReinforcePicker } from '../components/fleet/ReinforcePicker';
import type { GameContext } from '../components/GameLayout';
import { ErrorNotice, Page, Spinner } from '../components/ui';
import { MISSION_LABELS } from '../labels';
import { useNow } from '../state/live';
import { invalidateMilitary, useSystemNames } from './fleet';

export function ReinforceScreen() {
  const { galaxyId } = useOutletContext<GameContext>();
  const [params] = useSearchParams();
  const target = params.get('target') ?? '';
  const mission = params.get('mission') === 'raid' ? 'raid' : 'attack';
  const arriveAt = params.get('arriveAt') ?? '';
  const name = useSystemNames(galaxyId);
  const now = useNow();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const options = useReinforceOptions(
    galaxyId,
    target && arriveAt ? { targetSystemId: target, mission, arriveAt } : null
  );
  const send = useMutation({
    mutationFn: (input: {
      systemId: string;
      ships: { shipType: ShipType; count: number }[];
      confirmShieldLift: boolean;
    }) =>
      endpoints.placeFleetOrder(galaxyId, {
        originSystemId: input.systemId,
        targetSystemId: target,
        mission,
        ships: input.ships,
        arriveAt,
        confirmShieldLift: input.confirmShieldLift,
      }),
    onSuccess: () => {
      invalidateMilitary(queryClient, galaxyId);
      navigate(`/g/${galaxyId}/fleet`);
    },
  });
  return (
    <Page>
      <Link
        to={`/g/${galaxyId}/fleet`}
        className="inline-flex items-center gap-1 text-sm text-info"
      >
        <ArrowLeft className="size-4" aria-hidden /> Fleets
      </Link>
      <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
        Reinforce {mission}
      </h1>
      <p className="text-sm text-muted">
        {MISSION_LABELS[mission]} on {name(target)} · arrives{' '}
        {arriveAt ? new Date(arriveAt).toLocaleString() : '—'}
      </p>
      {options.isPending && options.fetchStatus !== 'idle' ? <Spinner /> : null}
      {options.error ? <ErrorNotice error={options.error} /> : null}
      {options.data ? (
        <ReinforcePicker
          options={options.data}
          name={name}
          now={now}
          pending={send.isPending}
          error={send.error}
          onSend={(systemId, ships, confirmShieldLift) =>
            send.mutate({ systemId, ships, confirmShieldLift })
          }
        />
      ) : null}
    </Page>
  );
}
